import { memo, type MouseEvent, type ReactNode } from "react";
import "./terms.style.css";

interface TermsMarkdownProps {
  content: string;
  /**
   * When true, renders compactly for inline use next to a checkbox. Use the
   * default (false) for the full detail body.
   */
  inline?: boolean;
  className?: string;
}

/**
 * GitHub-style heading slug: lowercase, drop punctuation, spaces → `-`.
 * `# 1. SEND User Agreement` → `1-send-user-agreement`, so a markdown index
 * written for GitHub (`[…](#1-send-user-agreement)`) resolves here too.
 * Mirrors `TermsMarkdown` in the production widgets.
 */
const slugify = (text: string): string =>
  text
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\p{M}\s-]/gu, "")
    .replace(/\s/g, "-");

const isAnchorHref = (href: string): boolean => href.startsWith("#");

/**
 * In-document anchor (e.g. a table of contents `[Section](#section)`): scroll
 * to the heading inside the widget's scroll container instead of letting the
 * browser touch the URL hash (which the SPA router would otherwise own).
 */
const scrollToAnchor = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
  const target = document.getElementById(decodeURIComponent(href.slice(1)));
  if (!target) return;
  event.preventDefault();
  target.scrollIntoView({ behavior: "smooth", block: "start" });
};

/** Strip inline markdown so heading slugs are built from visible text only. */
const plainText = (text: string): string =>
  text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1");

/**
 * Minimal, dependency-free markdown renderer for partner-configured Terms &
 * Conditions copy used by the gallery screens.
 *
 * The production widget uses `react-markdown`; the screenbook intentionally
 * avoids adding that dependency, so this supports just the constructs the
 * gallery mock terms exercise: headings (with GitHub-style ids), paragraphs,
 * bullet / numbered lists, `**bold**`, and `[label](url)` links. External
 * links open in a new tab so they never break in-widget navigation;
 * `#anchor` links scroll within the document so a markdown index works.
 */
const INLINE_PATTERN = /(\[[^\]]+\]\([^)]+\))|(\*\*[^*]+\*\*)/g;
const HEADING_PATTERN = /^(#{1,6})\s+(.*)$/;
const LIST_ITEM_PATTERN = /^(?:[-*]|\d+\.)\s+(.*)$/;

const renderInline = (text: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  for (const match of text.matchAll(INLINE_PATTERN)) {
    const index = match.index ?? 0;
    if (index > lastIndex) nodes.push(text.slice(lastIndex, index));
    const token = match[0];
    if (token.startsWith("[")) {
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      if (linkMatch) {
        const [, label, href] = linkMatch;
        nodes.push(
          isAnchorHref(href) ? (
            <a
              key={`a-${key++}`}
              href={href}
              onClick={(e) => scrollToAnchor(e, href)}
            >
              {label}
            </a>
          ) : (
            <a
              key={`a-${key++}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {label}
            </a>
          )
        );
      }
    } else {
      nodes.push(<strong key={`b-${key++}`}>{token.slice(2, -2)}</strong>);
    }
    lastIndex = index + token.length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
};

const renderBlocks = (content: string): ReactNode[] => {
  // Heading ids deduplicated with `-1`, `-2`, … like GitHub.
  const seen = new Map<string, number>();
  const headingId = (text: string): string | undefined => {
    const base = slugify(plainText(text));
    if (!base) return undefined;
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return count === 0 ? base : `${base}-${count}`;
  };

  const blocks = content
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean);

  return blocks.map((block, i) => {
    const heading = HEADING_PATTERN.exec(block);
    if (heading) {
      const Tag = `h${heading[1].length}` as "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
      return (
        <Tag key={i} id={headingId(heading[2])}>
          {renderInline(heading[2])}
        </Tag>
      );
    }

    const lines = block.split("\n");
    if (lines.every((line) => LIST_ITEM_PATTERN.test(line))) {
      const ordered = /^\d+\./.test(lines[0]);
      const Tag = ordered ? "ol" : "ul";
      return (
        <Tag key={i}>
          {lines.map((line, j) => (
            <li key={j}>{renderInline(LIST_ITEM_PATTERN.exec(line)?.[1] ?? "")}</li>
          ))}
        </Tag>
      );
    }

    return <p key={i}>{renderInline(block)}</p>;
  });
};

export const TermsMarkdown = memo(
  ({ content, inline = false, className }: TermsMarkdownProps) => {
    const cls = `terms-markdown${inline ? " terms-markdown--inline" : ""}${
      className ? ` ${className}` : ""
    }`;

    if (inline) {
      return <span className={cls}>{renderInline(content)}</span>;
    }

    return <div className={cls}>{renderBlocks(content)}</div>;
  }
);
